// Orchestrator for the Taxes & Fees tab. Section-level gating is handled inside
// each subsection — this component just wires data + save flow.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTaxSettings } from '@/hooks/parts-financial/useShopTaxSettings';
import { useShopTaxRules } from '@/hooks/parts-financial/useShopTaxRules';
import { useShopFeeRules } from '@/hooks/parts-financial/useShopFeeRules';
import { useTaxLookupStub } from '@/hooks/parts-financial/useTaxLookupStub';
import { useSaveTaxSettings, usePersistTaxSnapshot } from '@/hooks/parts-financial/useSaveTaxSettings';
import { TaxModeSection, type TaxMode } from './TaxModeSection';
import { AddressSourceSection } from './AddressSourceSection';
import { EffectiveTaxSection } from './EffectiveTaxSection';
import { TaxabilityRulesSection } from './TaxabilityRulesSection';
import { FeeRulesSection, type FeeRuleDraft } from './FeeRulesSection';
import { PreviewCalculatorSection } from './PreviewCalculatorSection';
import type { TaxabilityCategories } from '@/lib/parts-financial/calculator';
import type { TaxLookupAddress } from '@/lib/parts-financial/taxLookup';

// Phase 1 hardening (Option A): provider illusion removed. Flat-rate model only.
// All persisted records use provider='none'.
const TAX_PROVIDER = 'none' as const;

interface Props {
  shopId: string;
  address: Partial<TaxLookupAddress>;
}

export function TaxesFeesTab({ shopId, address }: Props) {
  const { user } = useAuth();
  const settingsQ = useShopTaxSettings(shopId);
  const rulesQ = useShopTaxRules(shopId);
  const feesQ = useShopFeeRules(shopId);
  const lookup = useTaxLookupStub();
  const save = useSaveTaxSettings();
  const persistSnapshot = usePersistTaxSnapshot();

  const [mode, setMode] = useState<TaxMode>('manual');
  const [combinedRate, setCombinedRate] = useState(0);
  const [stateRate, setStateRate] = useState(0);
  const [localRate, setLocalRate] = useState(0);
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);
  const [manualOverride, setManualOverride] = useState(true);
  const [lastLookupAt, setLastLookupAt] = useState<string | null>(null);
  const [categories, setCategories] = useState<TaxabilityCategories | null>(null);
  const [feeRules, setFeeRules] = useState<FeeRuleDraft[]>([]);

  // Hydrate from server
  useEffect(() => {
    if (!settingsQ.data) return;
    setMode(settingsQ.data.mode);
    setCombinedRate(Number(settingsQ.data.combined_rate || 0));
    setStateRate(Number(settingsQ.data.state_rate || 0));
    setLocalRate(Number(settingsQ.data.local_rate || 0));
    setSourceLabel(settingsQ.data.source_label);
    setManualOverride(settingsQ.data.manual_override);
    setLastLookupAt(settingsQ.data.last_lookup_at);
  }, [settingsQ.data]);

  useEffect(() => { if (rulesQ.data) setCategories(rulesQ.data.categories); }, [rulesQ.data]);

  useEffect(() => {
    if (feesQ.data) {
      setFeeRules(feesQ.data.map((r) => ({
        id: r.id, kind: r.kind, label: r.label, calc_type: r.calc_type,
        amount: Number(r.amount), cap_amount: r.cap_amount != null ? Number(r.cap_amount) : null,
        taxable: r.taxable, enabled: r.enabled,
      })));
    }
  }, [feesQ.data]);

  const isLoading = settingsQ.isLoading || rulesQ.isLoading || feesQ.isLoading;

  const handleLookup = async () => {
    if (!address.streetAddress || !address.city || !address.state || !address.postalCode || !address.country) return;
    try {
      const result = await lookup.mutateAsync(address as TaxLookupAddress);
      setCombinedRate(result.combinedRate);
      setStateRate(result.stateRate);
      setLocalRate(result.localRate);
      setSourceLabel(result.sourceLabel);
      setManualOverride(false);
      setLastLookupAt(result.lookedUpAt);
      await persistSnapshot.mutateAsync({
        shopId, address: address as TaxLookupAddress, result, userId: user?.id ?? null,
      });
      toast({ title: 'Suggested rates loaded', description: `Source: ${result.sourceLabel}. Review and save to apply.` });
    } catch (e: any) {
      toast({ title: 'Lookup failed', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
  };

  const handleValidate = () => {
    toast({ title: 'Address validated (stub)', description: 'Address format looks reasonable. Real validation coming in a later phase.' });
  };

  const handleEffectiveChange = (next: { combinedRate: number; stateRate: number; localRate: number }) => {
    setCombinedRate(next.combinedRate);
    setStateRate(next.stateRate);
    setLocalRate(next.localRate);
    setManualOverride(true);
    setSourceLabel('manual');
  };

  const handleSave = async () => {
    if (!categories) return;
    try {
      await save.mutateAsync({
        shopId,
        settings: {
          mode, provider: TAX_PROVIDER,
          combined_rate: combinedRate, state_rate: stateRate, local_rate: localRate,
          source_label: sourceLabel, manual_override: manualOverride,
          last_lookup_at: lastLookupAt,
        },
        rules: { categories },
        feeRules,
      });
      toast({ title: 'Taxes & fees saved' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
  };

  if (isLoading || !categories) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <TaxModeSection value={mode} onChange={setMode} />
      <AddressSourceSection
        address={address}
        onValidate={handleValidate}
        onLookup={handleLookup}
        isLookingUp={lookup.isPending}
        lastSourceLabel={sourceLabel}
        lastLookupAt={lastLookupAt}
      />
      <EffectiveTaxSection
        combinedRate={combinedRate} stateRate={stateRate} localRate={localRate}
        sourceLabel={sourceLabel} manualOverride={manualOverride}
        onChange={handleEffectiveChange}
      />
      <TaxabilityRulesSection value={categories} onChange={setCategories} />
      <FeeRulesSection value={feeRules} onChange={setFeeRules} />
      <PreviewCalculatorSection feeRules={feeRules} categories={categories} combinedRate={combinedRate} />

      <div className="flex justify-end pt-2">
        <Button onClick={handleSave} disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Taxes & Fees
        </Button>
      </div>
    </div>
  );
}
